import React, { FC, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentMessage,
  AgentStreamEvents,
  UserMessageEvent,
} from '@dreamai/types';
import { usePusher } from '../../../contexts/PusherContext';

interface ThirdPartyMessageHandlerProps {
  threadId: string | null;
  setMessages: (
    messages:
      | AgentMessage[]
      | ((prevMessages: AgentMessage[]) => AgentMessage[]),
  ) => void;
}

/**
 * Component that listens for user messages from all sources (web app, WhatsApp, phone)
 * and adds them to the message list for real-time collaboration
 */
const ThirdPartyMessageHandler: FC<ThirdPartyMessageHandlerProps> = ({
  threadId,
  setMessages,
}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const currentUserId = user?.sub;
  const { pusher } = usePusher();

  const handleUserMessage = useCallback(
    (data: UserMessageEvent) => {
      console.log('Received user message:', data);

      // Filter out messages from the current user to avoid duplicates
      // (we already show optimistic messages when you send)
      if (data.userId === currentUserId) {
        console.log('Skipping own message to avoid duplicate');
        return;
      }

      // Create a new user message with full metadata
      const newMessage: AgentMessage = {
        role: 'user',
        content: data.message,
        timestamp: data.timestamp,
        metadata: {
          userId: data.userId,
          userName: data.userName,
        },
      };

      // Add the message to the list
      setMessages((prevMessages) => [...prevMessages, newMessage]);
    },
    [setMessages, currentUserId],
  );

  useEffect(() => {
    if (!threadId || !pusher) return;

    // Subscribe to the thread's channel
    const channel = pusher.subscribe(
      `${process.env.REACT_APP_PUSHER_ENV}-private-thread-${threadId}`,
    );

    // Listen for user message events
    channel.bind(AgentStreamEvents.USER_MESSAGE, handleUserMessage);

    return () => {
      // Unbind event and unsubscribe from channel when component unmounts
      channel.unbind(AgentStreamEvents.USER_MESSAGE);
      pusher.unsubscribe(
        `${process.env.REACT_APP_PUSHER_ENV}-private-thread-${threadId}`,
      );
    };
  }, [threadId, pusher, handleUserMessage, getAccessTokenSilently]);

  // This component doesn't render anything
  return null;
};

export default ThirdPartyMessageHandler;
